import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AgrimotorService } from '../../agrimotor.service';
import { QuoteService } from '../../../quote.service';

@Component({
  selector: 'app-ag-vehicle-form',
  templateUrl: './vehicle-form.component.html',
  styleUrls: ['./vehicle-form.component.css', '../../../quote.component.css']
})
export class AgVehicleFormComponent implements OnInit, OnChanges {
  @Input() vehicle;
  @Input() staticData;
  @Input() authDetails;
  @Input() modelObj;
  @Input() index;
  @Output() driverAdded = new EventEmitter();
  @Output() driverRemoved = new EventEmitter();

  frmFields: FormGroup;
  submitted = false;
  selectedType = '';
  showDrivers = false;
  showAddDriverDialog = false;

  vehicleTypes = [
    { value: 'agricultural', label: 'Agricultural' },
    { value: 'cars', label: 'Car' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'horseboxes', label: 'Horsebox' },
    { value: 'trailers', label: 'Trailer / Implement' },
  ];

  // Standard exclusions shown alongside the "Use of Vehicle" summary, matching the
  // wording used on the insurer's own agri portal.
  showExclusions = false;
  readonly standardExclusions = [
    'Use by any other person to travel to and/or from their place of work and/or study and for any associated business use',
    'Carriage of passengers and/or goods for hire and/or reward',
    'Carriage of any horse, animal and/or other living creatures for hire and/or reward',
    'Towing for reward a mechanically propelled vehicle',
    'Racing, pace-setting, speed trials, motor rallies, competitions or trials',
    'Hire of the vehicle',
    'Use in connection with the motor trade',
    'Use in connection with the undertaking of contract work',
    'Use in connection with any other business',
    'Use to secure the release of a motor vehicle other than the vehicle identified above by its registration mark, which has been seized by, or on behalf of, any government or public authority',
  ];

  private readonly extraAgriculturalBodyTypes = [
    'Combine Harvester',
    'Forest Harvester',
    'Lawn Mower',
    'Loader',
    'Loading Shovel',
    'Other Harvester',
    'Potato/ Beet Harvester',
    'Viner',
  ];

  constructor(
    private fb: FormBuilder,
    private agService: AgrimotorService,
    private quoteService: QuoteService,
    private _snackBar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    this.buildForm();
    this.mergeExtraAgriculturalBodyTypes();
    if (this.vehicle) {
      this.patchValues();
    }
    this.applyContractWork();
    this.updateDynamicValidators();

    // Track type changes explicitly so template conditions are always up to date
    this.frmFields.get('type').valueChanges.subscribe(type => {
      this.selectedType = type || '';
      // Re-check drivers visibility when type changes (restrictions may no longer apply)
      this.checkDrivingRestriction(this.frmFields.get('driving_restriction').value);
      this.updateDynamicValidators();
    });

    // Track driving restriction changes to show/hide drivers section
    this.frmFields.get('driving_restriction').valueChanges.subscribe(val => {
      this.checkDrivingRestriction(val);
    });
    this.frmFields.get('extended_use.livestock_carriage').valueChanges
      .subscribe(() => this.updateDynamicValidators());
    this.frmFields.get('extended_use.driver_business_use').valueChanges
      .subscribe(() => this.updateDynamicValidators());
    this.frmFields.get('has_modifications').valueChanges
      .subscribe(() => this.updateDynamicValidators());
    this.frmFields.get('is_road_registered').valueChanges.subscribe(() => this.updateDynamicValidators());
    this.frmFields.get('is_registered_owner').valueChanges.subscribe(() => this.updateDynamicValidators());
    this.frmFields.get('is_limited_use').valueChanges.subscribe(() => this.updateDynamicValidators());
    this.frmFields.get('is_kept_overnight').valueChanges.subscribe(() => {
      this.updateDynamicValidators();
      this.resetAddressLookups();
    });

    this.frmFields.valueChanges.subscribe(val => {
      if (this.vehicle) {
        Object.assign(this.vehicle, val);
        // Boolean conversions (API expects booleans; form uses Yes/No strings)
        this.vehicle.green_card = val.green_card === 'Yes' ? true : val.green_card === 'No' ? false : '';
        this.vehicle.road_registered = val.is_road_registered === 'Yes';
        this.vehicle.limited_use = val.is_limited_use === 'Yes';
        // Owner: only populate when proposer is NOT the registered owner
        this.vehicle.owner = val.is_registered_owner === 'No' ? (val.owner || '') : '';
        this.vehicle.owner_name = val.is_registered_owner === 'No' ? (val.owner_name || '') : '';
        // Overnight addresses: only sent when the vehicle is NOT kept at the
        // correspondence address. `postcode` keeps the first of them — it is the field
        // the agri-api rates on and the one every read-only view reads.
        this.syncOvernightAddresses(val);
        // extended_use mapping (contract_work is disabled so pull its real value separately)
        if (val.extended_use) {
          this.vehicle.extended_use = {
            ...val.extended_use,
            contract_work: this.frmFields.get('extended_use.contract_work').value,
          };
        }
        // mods
        if (val.mods !== undefined) {
          this.vehicle.mods = val.mods;
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.staticData) {
      this.mergeExtraAgriculturalBodyTypes();
    }
    if (this.frmFields && this.vehicle) {
      this.patchValues();
      this.applyContractWork();
      this.updateDynamicValidators();
    }
  }

  /**
   * Re-read the bound vehicle object into the form. Used when the page-level
   * declaration questions write defaults straight onto the vehicles — @Input identity
   * doesn't change, so ngOnChanges wouldn't fire on its own.
   */
  refreshFromVehicle() {
    if (!this.frmFields || !this.vehicle) { return; }
    this.patchValues();
    this.applyContractWork();
    this.updateDynamicValidators();
  }

  private mergeExtraAgriculturalBodyTypes() {
    if (this.staticData?.body_types) {
      if (!this.staticData.body_types.agricultural) {
        this.staticData.body_types.agricultural = [];
      }
      for (const type of this.extraAgriculturalBodyTypes) {
        if (!this.staticData.body_types.agricultural.includes(type)) {
          this.staticData.body_types.agricultural.push(type);
        }
      }
    }
  }

  buildForm() {
    this.frmFields = this.fb.group({
      type: ['', Validators.required],
      // Agricultural: road registered
      is_road_registered: [''],
      // Identifiers
      registration_number: [''],
      vin: [''],
      // Core fields
      make: ['', Validators.required],
      model: ['', Validators.required],
      year: [''],
      body_type: [''],
      weight: [''],          // GVW – horseboxes, commercial
      value: ['', Validators.required],
      // Cover
      cover_type: [''],
      is_limited_use: [''],  // UI helper → limited_use boolean for cars/commercial
      driving_restriction: [''],
      // Extended use
      extended_use: this.fb.group({
        driver_business_use: [false],
        livestock_carriage: [false],
        contract_work: [{ value: false, disabled: true }],
        horse_transporter: [''],
      }),
      // Mileage / tracker / NCD
      mileage: [''],
      tracker: [''],
      ncd: [''],
      // Green card / owner / overnight
      green_card: [''],
      is_registered_owner: [''],
      owner: [''],
      owner_name: [''],
      is_kept_overnight: [''],
      // A vehicle can be kept overnight at more than one address (the insurer's own
      // portal allows several), so this is a list of full addresses rather than the
      // single postcode string it replaced.
      overnight_addresses: this.fb.array([]),
      // Modifications
      has_modifications: [''],
      mods: this.fb.array([]),
    });
  }

  // The API round-trips these Yes/No radio fields as real booleans (not the 'Yes'/'No'
  // strings the form writes), so patching must accept both representations.
  //
  // It also stringifies some of them on the way back — a saved quote returns
  // `"road_registered": "true"`, not `true` — so the string spellings count too. Without
  // them a strict `=== true` test failed and the radio group rendered blank on edit.
  private toYesNo(val: any): string {
    if (val === true || val === 'Yes' || val === 'true') { return 'Yes'; }
    if (val === false || val === 'No' || val === 'false') { return 'No'; }
    return '';
  }

  /**
   * True once this vehicle has been through a completed save.
   *
   * green_card is the reference point: it is mandatory for exactly the same vehicle classes
   * as the two questions below it, the API DOES persist it (as a real boolean), and nextTab()
   * refuses to save while any vehicle form is invalid — so a definite true/false here means
   * this vehicle's owner/overnight answers were given and accepted at the same time. An empty
   * green_card means a fresh vehicle, whose Yes/No questions must stay unanswered.
   */
  private get isSavedVehicle(): boolean {
    return this.toYesNo(this.vehicle && this.vehicle.green_card) !== '';
  }

  /**
   * The agri-api stores no is_registered_owner / is_kept_overnight flag — the vehicle schema
   * only carries the value each one guards (`owner`/`owner_name`, `postcode`). Both came back
   * undefined on edit, so toYesNo() returned '' and the radio group rendered with nothing
   * selected. This recovers the answer from what the API did keep, mirroring the fallbacks
   * already used for is_road_registered, is_limited_use and has_modifications.
   *
   * A populated value is proof of 'No' on its own — the write path only fills these in when the
   * answer was 'No'. An empty one means 'Yes' only on a vehicle that has actually been saved;
   * on a new vehicle it just means "not answered yet", so it stays blank and the mandatory-field
   * highlighting still fires.
   */
  private yesNoFromPersisted(persistedValue: any): string {
    if (persistedValue) { return 'No'; }
    return this.isSavedVehicle ? 'Yes' : '';
  }

  patchValues() {
    const modsArray = this.frmFields.get('mods') as FormArray;
    modsArray.clear();
    if (this.vehicle.mods && this.vehicle.mods.length) {
      this.vehicle.mods.forEach(m => modsArray.push(this.fb.control(m, Validators.required)));
    }

    this.rebuildOvernightAddresses();

    this.selectedType = this.vehicle.type || '';

    // Only agricultural vehicles are asked whether they are road registered, and every empty
    // vehicle carries road_registered:false — so a bare 'No' is only meaningful for that type.
    const roadRegistered = this.vehicle.type === 'agricultural'
      ? this.toYesNo(this.vehicle.road_registered)
      : (this.toYesNo(this.vehicle.road_registered) === 'Yes' ? 'Yes' : '');

    // The question must never arrive with an option already selected, so only an explicit
    // answer restores it: `is_limited_use` (the Yes/No we now persist) or a limited_use of
    // true. A limited_use of `false` is what every empty vehicle carries, so it is never
    // treated as an answer — the radio stays blank and the mandatory highlighting fires.
    const limitedUse = this.toYesNo(this.vehicle.limited_use) === 'Yes' ? 'Yes' : '';

    this.frmFields.patchValue({
      type: this.vehicle.type || '',
      is_road_registered: this.toYesNo(this.vehicle.is_road_registered) || roadRegistered,
      registration_number: this.vehicle.registration_number || '',
      vin: this.vehicle.vin || '',
      make: this.vehicle.make || '',
      model: this.vehicle.model || '',
      year: this.vehicle.year || '',
      body_type: this.vehicle.body_type || '',
      weight: this.vehicle.weight || '',
      value: this.vehicle.value || '',
      cover_type: this.vehicle.cover_type || '',
      is_limited_use: this.toYesNo(this.vehicle.is_limited_use) || limitedUse,
      driving_restriction: this.vehicle.driving_restriction || '',
      extended_use: {
        driver_business_use: this.vehicle.extended_use?.driver_business_use || false,
        livestock_carriage: this.vehicle.extended_use?.livestock_carriage || false,
        contract_work: this.vehicle.extended_use?.contract_work || false,
        horse_transporter: this.toYesNo(this.vehicle.extended_use?.horse_transporter),
      },
      mileage: this.vehicle.mileage || '',
      tracker: this.vehicle.tracker || '',
      ncd: this.vehicle.ncd || '',
      // Do NOT default these — they must start unselected (not pre-populated)
      green_card: this.toYesNo(this.vehicle.green_card),
      is_registered_owner: this.toYesNo(this.vehicle.is_registered_owner)
        || this.yesNoFromPersisted(this.vehicle.owner || this.vehicle.owner_name),
      owner: this.vehicle.owner || '',
      owner_name: this.vehicle.owner_name || '',
      is_kept_overnight: this.toYesNo(this.vehicle.is_kept_overnight)
        || this.yesNoFromPersisted(this.vehicle.postcode),
      has_modifications: this.toYesNo(this.vehicle.has_modifications) || (this.vehicle.mods && this.vehicle.mods.length > 0 ? 'Yes' : ''),
    }, { emitEvent: false });

    // Check if existing driving restriction requires drivers section
    this.checkDrivingRestriction(this.vehicle.driving_restriction);
  }

  // ─── Contract work (auto-derived, non-editable) ────────────────────────────
  // Driven by the proposer's "Agricultural / Forestry Contracting Percentage": anything
  // other than "No Contracting" means contract work is in play.
  //
  // Two things were wrong here. The sentinel was compared against '0%', but the value
  // the lists endpoint actually serves is 'No Contracting' — and the percentage was
  // only read from `proposer.contracting_percentage`, while the API returns it at the
  // root of the application. Either miss left the tick box clear on a contracting quote.
  applyContractWork() {
    const contractingPercentage = this.modelObj?.proposer?.contracting_percentage
      || this.modelObj?.contracting_percentage;
    const normalised = (contractingPercentage || '').toString().trim().toLowerCase();
    const noContracting = ['', 'no contracting', 'none', '0%', '0'];
    const doesContracting = !noContracting.includes(normalised);
    this.frmFields.get('extended_use.contract_work').setValue(doesContracting, { emitEvent: false });
    if (this.vehicle) {
      if (!this.vehicle.extended_use) { this.vehicle.extended_use = {}; }
      this.vehicle.extended_use.contract_work = doesContracting;
    }
  }

  // ─── Dynamic per-type required-field validators ────────────────────────────
  private requiredFieldsByType: { [key: string]: string[] } = {
    // cover_type belongs here too — the template always marks it required, but without
    // a validator on the control an Agri vehicle could be progressed without one.
    agricultural: ['is_road_registered', 'year', 'body_type', 'cover_type', 'green_card', 'is_registered_owner', 'is_kept_overnight'],
    cars: ['registration_number', 'year', 'body_type', 'cover_type', 'driving_restriction', 'is_limited_use', 'green_card', 'mileage', 'tracker', 'is_registered_owner', 'is_kept_overnight', 'has_modifications', 'ncd'],
    commercial: ['registration_number', 'year', 'weight', 'body_type', 'cover_type', 'driving_restriction', 'is_limited_use', 'green_card', 'mileage', 'tracker', 'is_registered_owner', 'is_kept_overnight', 'has_modifications', 'ncd'],
    // No 'body_type' — the agri-api static data has no body_types.horseboxes list;
    // horseboxes are classified by gross vehicle weight instead (see 'weight' below).
    horseboxes: ['registration_number', 'year', 'weight', 'cover_type', 'driving_restriction', 'green_card', 'mileage', 'tracker', 'is_registered_owner', 'is_kept_overnight', 'ncd'],
    trailers: ['vin', 'body_type'],
  };

  private dynamicFieldNames = [
    'is_road_registered', 'registration_number', 'vin', 'year', 'body_type', 'weight',
    'cover_type', 'driving_restriction', 'is_limited_use', 'green_card', 'mileage', 'tracker',
    'is_registered_owner', 'is_kept_overnight', 'has_modifications',
  ];

  updateDynamicValidators() {
    if (!this.frmFields) { return; }
    const type = this.frmFields.get('type').value;
    const required = new Set(this.requiredFieldsByType[type] || []);

    // Agricultural: reg number required if road registered, otherwise VIN
    if (type === 'agricultural') {
      if (this.frmFields.get('is_road_registered').value === 'No') {
        required.add('vin');
      } else {
        required.add('registration_number');
      }
    }

    // NCD is required by the agri-api for cars / commercial / horseboxes on every
    // rating type EXCEPT fleet — including "No Previous Agricultural Insurance"
    // ('none'), and regardless of limited use. Gating it on rating_type === 'ncd'
    // meant the calculate call failed with
    //   007 Missing data /data/attributes/vehicles/N/ncd
    // on any other rating type, which the quotation page then showed as a £0 premium.
    // The options list includes '0', so "no NCD" is still expressible.
    const ncdControl = this.frmFields.get('ncd');
    const ncdRequired = this.requiredFieldsByType[type]?.includes('ncd') && this.ncdApplies;
    ncdControl.setValidators(ncdRequired ? [Validators.required] : []);
    ncdControl.updateValueAndValidity({ emitEvent: false });

    this.dynamicFieldNames.forEach(name => {
      const control = this.frmFields.get(name);
      if (!control) { return; }
      control.setValidators(required.has(name) ? [Validators.required] : []);
      control.updateValueAndValidity({ emitEvent: false });
    });

    // Owner: required only when proposer is not the registered owner
    const ownerControl = this.frmFields.get('owner');
    ownerControl.setValidators(this.frmFields.get('is_registered_owner').value === 'No' ? [Validators.required] : []);
    ownerControl.updateValueAndValidity({ emitEvent: false });

    // Overnight addresses: every listed postcode is required, but only while the
    // vehicle is not kept at the correspondence address.
    this.updateOvernightAddressValidators();

    // Horse Transporter: mandatory once "Carriage of any horse, animal and/or other
    // living creatures for hire and/or reward" has been selected.
    const horseTransporterControl = this.frmFields.get('extended_use.horse_transporter');
    horseTransporterControl.setValidators(
      this.frmFields.get('extended_use.livestock_carriage').value ? [Validators.required] : []
    );
    horseTransporterControl.updateValueAndValidity({ emitEvent: false });

    // Modifications: saying "Yes" means at least one has to be named.
    const modsControl = this.frmFields.get('mods');
    modsControl.setValidators(
      this.frmFields.get('has_modifications').value === 'Yes'
        ? [(control: AbstractControl) => {
            const chosen = ((control as FormArray).controls || []).filter(c => !!c.value);
            return chosen.length > 0 ? null : { required: true };
          }]
        : []
    );
    modsControl.updateValueAndValidity({ emitEvent: false });
  }

  /** Fleet-experience-rated quotes are the only ones that don't collect NCD. */
  get ncdApplies(): boolean {
    return this.modelObj?.rating_type !== 'fleet';
  }

  get f() { return this.frmFields.controls; }
  get modsArray() { return this.frmFields.get('mods') as FormArray; }
  get currentType() { return this.frmFields.get('type')?.value || ''; }

  isType(...types: string[]) {
    return types.includes(this.currentType);
  }

  addMod() {
    this.modsArray.push(this.fb.control('', Validators.required));
  }

  removeMod(i: number) {
    this.modsArray.removeAt(i);
  }

  /** Modifications that have actually been chosen (blank rows don't count). */
  get namedModCount(): number {
    return this.modsArray.controls.filter(c => !!c.value).length;
  }

  lookupVehicle() {
    const regNum = this.frmFields.get('registration_number').value;
    if (regNum) {
      // Clear existing fields before lookup
      this.frmFields.patchValue({ make: '', model: '', year: '', value: '', weight: '' });

      this.agService.getVehicleDetails(regNum, this.authDetails)
        .subscribe((response: any) => {
          // The agri-api wraps results in `meta` (matching getStaticData/getUniqueID
          // elsewhere in this module, and how the sibling Amline lookup reads `res.meta`)
          // — not `data`, which was never actually populated by this endpoint.
          if (response.meta) {
            this.frmFields.patchValue({
              make: response.meta.make || '',
              model: response.meta.model || '',
              year: response.meta.year || '',
              value: response.meta.value || '',
              body_type: response.meta.body_type || this.frmFields.get('body_type').value,
              weight: response.meta.weight || this.frmFields.get('weight').value,
            });
          } else {
            this._snackBar.open('No vehicle details found for that registration number.', '', { duration: 5000, panelClass: 'text-danger' });
          }
        }, () => {
          this._snackBar.open('Error looking up vehicle details. Please enter them manually.', '', { duration: 5000, panelClass: 'text-danger' });
        });
    }
  }

  // ─── Driving restriction options (Individual-only options hidden for companies) ──
  get filteredDrivingRestrictions() {
    const list = this.selectedType === 'commercial'
      ? (this.staticData?.driving_restrictions_commercial || [])
      : (this.staticData?.driving_restrictions_personal || []);
    if (this.modelObj?.proposer?.proposer_type !== 'company') { return list; }
    const individualOnly = ['insured only', 'insured and spouse', 'insured and named'];
    return list.filter(d => !individualOnly.includes((d || '').toString().trim().toLowerCase()));
  }

  // --- Overnight addresses ----------------------------------------------------
  // A vehicle may be kept overnight at several addresses, so these are held in a
  // FormArray with one lookup panel each.
  //
  // The lookup is proxied through the backend (avoids CORS and the dead direct
  // getAddress.io 'find' endpoint) - the same address/search + address/get pattern used
  // by the proposer address lookup. Picking a result fills in the full address, not just
  // the postcode, so the broker can see which address they chose.

  /** Per-address lookup state, index-aligned with the overnight_addresses FormArray. */
  addressLookups: {
    loading: boolean; message: string; list: any[]; show: boolean; verified: boolean;
  }[] = [];

  /** Beyond this the list stops being a list of exceptions. */
  readonly maxOvernightAddresses = 5;

  get overnightAddresses(): FormArray {
    return this.frmFields.get('overnight_addresses') as FormArray;
  }

  private newAddressGroup(address: any = {}): FormGroup {
    return this.fb.group({
      postcode: [(address.postcode || '').toString().toUpperCase()],
      line1: [address.line1 || ''],
      line2: [address.line2 || ''],
      line3: [address.line3 || ''],
      town: [address.town || ''],
      county: [address.county || ''],
    });
  }

  /** Keeps addressLookups the same length as the FormArray it shadows. */
  private syncLookupState(verified: boolean[] = []) {
    const length = this.overnightAddresses.length;
    while (this.addressLookups.length < length) {
      this.addressLookups.push({ loading: false, message: '', list: [], show: false, verified: null });
    }
    this.addressLookups.length = length;
    verified.forEach((v, i) => { if (this.addressLookups[i]) { this.addressLookups[i].verified = v; } });
  }

  private resetAddressLookups() {
    this.addressLookups.forEach(state => {
      state.verified = null;
      state.show = false;
      state.message = '';
      state.list = [];
    });
  }

  /**
   * Re-reads the bound vehicle into the address list. Quotes saved before this page held
   * full addresses only carry the single `postcode` string, so that is the fallback seed
   * and a reopened quote keeps the postcode it was quoted on.
   */
  private rebuildOvernightAddresses() {
    const array = this.overnightAddresses;
    // Read the vehicle BEFORE emptying the array: clearing it emits a value change, and
    // the write-back below would otherwise wipe the very data being read back in.
    const saved = Array.isArray(this.vehicle.overnight_addresses) ? this.vehicle.overnight_addresses : [];
    const seed = saved.length
      ? saved
      : (this.vehicle.postcode ? [{ postcode: this.vehicle.postcode }] : []);
    this.rebuildingAddresses = true;
    while (array.length) { array.removeAt(array.length - 1); }
    seed.slice(0, this.maxOvernightAddresses).forEach(a => array.push(this.newAddressGroup(a)));
    if (array.length === 0) { array.push(this.newAddressGroup()); }
    this.rebuildingAddresses = false;
    // A postcode that came back from a save was accepted at the time, so it shows as
    // confirmed rather than making the broker search for it again.
    this.syncLookupState(array.controls.map(c => (c.get('postcode').value ? true : null)));
  }

  private updateOvernightAddressValidators() {
    const required = this.frmFields.get('is_kept_overnight').value === 'No';
    if (required && this.overnightAddresses.length === 0) {
      this.overnightAddresses.push(this.newAddressGroup());
      this.syncLookupState();
    }
    this.overnightAddresses.controls.forEach(group => {
      const postcode = group.get('postcode');
      postcode.setValidators(required ? [Validators.required] : []);
      postcode.updateValueAndValidity({ emitEvent: false });
    });
  }

  /**
   * Writes the address list back onto the bound vehicle. `postcode` is kept in step with
   * the first address, so the agri-api (which takes one postcode per vehicle) and the
   * read-only quote views carry on working unchanged.
   */
  /** Set while the array is being rebuilt from the vehicle, so the write-back stands down. */
  private rebuildingAddresses = false;

  private syncOvernightAddresses(val: any) {
    if (this.rebuildingAddresses) { return; }
    if (val.is_kept_overnight !== 'No') {
      this.vehicle.overnight_addresses = [];
      this.vehicle.postcode = '';
      return;
    }
    const addresses = (val.overnight_addresses || [])
      .filter((a: any) => (a.postcode || '').toString().trim())
      .map((a: any) => ({ ...a, postcode: a.postcode.toString().trim().toUpperCase() }));
    this.vehicle.overnight_addresses = addresses;
    this.vehicle.postcode = addresses.length ? addresses[0].postcode : '';
  }

  addAnotherAddress() {
    if (this.overnightAddresses.length >= this.maxOvernightAddresses) {
      this._snackBar.open(
        'A maximum of ' + this.maxOvernightAddresses + ' overnight addresses can be added per vehicle.',
        '', { duration: 5000, panelClass: 'text-danger' });
      return;
    }
    this.overnightAddresses.push(this.newAddressGroup());
    this.syncLookupState();
    this.updateOvernightAddressValidators();
  }

  removeOvernightAddress(index: number) {
    if (this.overnightAddresses.length <= 1) { return; }
    this.overnightAddresses.removeAt(index);
    this.addressLookups.splice(index, 1);
    this.syncLookupState();
  }

  /** Clears one row so a different address can be searched for without losing the others. */
  clearOvernightAddress(index: number) {
    this.overnightAddresses.at(index).reset(
      { postcode: '', line1: '', line2: '', line3: '', town: '', county: '' });
    const state = this.addressLookups[index];
    if (state) {
      state.verified = null;
      state.show = false;
      state.message = '';
      state.list = [];
    }
  }

  /** The address lines of one row, as a single line for display. */
  formattedAddress(index: number): string {
    const a = this.overnightAddresses.at(index).value;
    return [a.line1, a.line2, a.line3, a.town, a.county]
      .map(part => (part || '').toString().trim())
      .filter(part => part)
      .join(', ');
  }

  lookupPostcode(index: number) {
    const group = this.overnightAddresses.at(index);
    const state = this.addressLookups[index];
    if (!group || !state) { return; }
    const postcode = (group.get('postcode').value || '').trim();
    if (!postcode) {
      state.message = 'Please enter a postcode to search.';
      return;
    }
    state.loading = true;
    state.message = '';
    this.quoteService.searchAddress(postcode)
      .subscribe(
        (response: any) => {
          state.loading = false;
          const raw = response?.records || response?.addresses || [];
          const records = Array.isArray(raw) ? raw : [];
          if (records.length > 0) {
            state.list = records;
            state.show = true;
            state.verified = true;
            state.message = '';
          } else {
            state.list = [];
            state.show = false;
            state.verified = false;
            state.message = response?.errormessage
              || 'No addresses found for that postcode. Please check it.';
          }
        },
        () => {
          state.loading = false;
          state.list = [];
          state.show = false;
          state.verified = null;
          state.message = 'The address lookup service is unavailable. Please type the postcode in manually.';
        }
      );
  }

  selectPostcodeAddress(index: number, address: any) {
    if (!address) { return; }
    const group = this.overnightAddresses.at(index);
    const state = this.addressLookups[index];
    if (!group || !state) { return; }
    const searchedPostcode = group.get('postcode').value;
    if (address.id != null && address.id !== '') {
      this.quoteService.fetchAddressById(address.id).subscribe(
        (full: any) => {
          this.applyAddressToGroup(index, full, searchedPostcode);
        },
        () => {
          state.show = false;
          state.message = 'Could not retrieve that address. Please type the postcode in manually.';
        }
      );
      return;
    }
    this.applyAddressToGroup(index, address, searchedPostcode);
  }

  private applyAddressToGroup(index: number, a: any, fallbackPostcode: string) {
    const group = this.overnightAddresses.at(index);
    const state = this.addressLookups[index];
    if (!group || !a) { return; }
    const postcode = a.postcode ?? a.Postcode ?? fallbackPostcode ?? '';
    group.patchValue({
      postcode: (postcode || '').toString().toUpperCase(),
      line1: a.line1 ?? a.line_1 ?? a.Line1 ?? '',
      line2: a.line2 ?? a.line_2 ?? a.Line2 ?? '',
      line3: a.line3 ?? a.line_3 ?? a.Line3 ?? '',
      town: a.town_or_city ?? a.town ?? a.Town ?? '',
      county: a.county ?? a.County ?? '',
    });
    if (state) {
      state.verified = true;
      state.show = false;
      state.message = '';
    }
  }

  useCorrespondenceAddress(index: number) {
    const address = this.modelObj?.proposer?.address;
    if (!address?.postcode) { return; }
    this.applyAddressToGroup(index, address, address.postcode);
    const state = this.addressLookups[index];
    if (state) { state.list = []; }
  }


  // ─── Named Driver logic ──────────────────────────────────────────────────────

  private namedDriverRestrictions = [
    'insured and spouse',
    'insured and named',
    'named drivers',
    'named drivers excluding policyholder',
    'any driver aged over 21 other than those named',
    'any driver aged over 25 other than those named',
    'any driver aged over 30 other than those named',
  ];

  checkDrivingRestriction(value: string) {
    const typesWithRestriction = ['horseboxes', 'cars', 'commercial'];
    this.showDrivers = typesWithRestriction.includes(this.selectedType)
      && this.namedDriverRestrictions.includes((value || '').toLowerCase());
    if (!this.showDrivers) {
      this.showAddDriverDialog = false;
    }
  }

  onDriverAdded(event) {
    this.driverAdded.emit({ vehicleIndex: this.index, ...event });
    this.showAddDriverDialog = false;
  }

  removeDriver(driverIndex: number) {
    this.driverRemoved.emit({ vehicleIndex: this.index, driverIndex, target: 'drivers' });
  }

  // ─── Extended-use drivers (personal / business use by someone other than the
  // proposer). Kept separate from the named-driver list because the API models them
  // as `extended_drivers`. ──────────────────────────────────────────────────────
  showAddExtendedDriverDialog = false;

  onExtendedDriverAdded(event) {
    this.driverAdded.emit({ vehicleIndex: this.index, ...event });
    this.showAddExtendedDriverDialog = false;
  }

  removeExtendedDriver(driverIndex: number) {
    this.driverRemoved.emit({ vehicleIndex: this.index, driverIndex, target: 'extended' });
  }

  /** True when this vehicle still needs driver detail before the quote can proceed. */
  get missingRequiredDrivers(): boolean {
    if (this.showDrivers && (!this.vehicle?.drivers || this.vehicle.drivers.length === 0)) { return true; }
    if (this.frmFields.get('extended_use.driver_business_use').value
        && (!this.vehicle?.extended_drivers || this.vehicle.extended_drivers.length === 0)) { return true; }
    return false;
  }
}
